/**
 * ChargesTableView - Tabela reutilizável de cobranças financeiras
 * Segue mesmo padrão visual do ContractsTableView e CustomerDealsTab:
 * - Tabela nativa HTML com scroll horizontal/vertical
 * - Header sticky, ordenação, filtros por status
 * - Barra lateral colorida por status de pagamento
 * - Colunas condicionais via props
 * 
 * @version 2.2.0
 * RELEASE: 2026-09-18 v2.2.0 - Coluna opcional de cliente/proprietário passa a ser exibida antes
 *          da coluna Descrição (colgroup, cabeçalho e célula), conforme padrão de leitura das
 *          listagens financeiras. Larguras e rótulo configurável inalterados.
 * RELEASE: 2026-03-15 v1.0.0 - Criação do componente (unificação visual)
 */
import React, { useState, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  DollarSign,
  FileText,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn, parseDateLocal } from '@/lib/utils';
import type { FinanceiroCobranca, FinanceiroStatus, TipoCobranca } from '@/hooks/useFinanceiroCobrancas';
import type { PaginatedCobranca } from '@/hooks/useFinanceiroCobrancasPaginated';
import { TIPOS_PAGAMENTO } from '@/hooks/useFinanceiroMeiosPagamento';

/**
 * Tipo unificado para cobranças na tabela
 * Aceita tanto FinanceiroCobranca (hook direto) quanto PaginatedCobranca (RPC)
 */
type ChargeItem = FinanceiroCobranca | PaginatedCobranca;
import { ChargeDetailsModal } from '@/components/financial/ChargeDetailsModal';

// ============================================================================
// CONSTANTES E MAPEAMENTOS
// ============================================================================

/** Mapeamento de status para labels e cores (badges) */
const STATUS_CONFIG: Record<FinanceiroStatus, { label: string; color: string }> = {
  aguardando_emissao: { label: 'Aguardando emissão', color: 'bg-slate-100 text-slate-700' },
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
  edicao_andamento: { label: 'Edição em andamento', color: 'bg-amber-100 text-amber-800' },
  aguardando_pagamento: { label: 'Aguardando Pagamento', color: 'bg-orange-100 text-orange-800' },
  pago: { label: 'Pago', color: 'bg-green-100 text-green-800' },
  parcial: { label: 'Parcial', color: 'bg-blue-100 text-blue-800' },
  vencido: { label: 'Vencido', color: 'bg-red-100 text-red-800' },
  cancelado: { label: 'Cancelado', color: 'bg-muted text-muted-foreground' },
  processando_cancelamento: { label: 'Processando cancelamento', color: 'bg-amber-100 text-amber-800' },
  falha_cancelamento: { label: 'Falha ao cancelar', color: 'bg-red-100 text-red-800' },
};

/** Mapeamento de tipo de cobrança */
const TIPO_CONFIG: Record<TipoCobranca, string> = {
  unica: 'Única',
  parcelada: 'Parcelada',
  recorrente: 'Recorrente',
};

// ============================================================================
// TIPOS
// ============================================================================

/** Props do componente */
interface ChargesTableViewProps {
  /** Array de cobranças (aceita FinanceiroCobranca ou PaginatedCobranca) */
  charges: ChargeItem[];
  /** Se está carregando */
  isLoading: boolean;
  /** Mensagem customizada para estado vazio */
  emptyMessage?: string;
  /** Ação para estado vazio (botão de criar, etc) — mesmo padrão do ContractsTableView */
  emptyAction?: React.ReactNode;
  /** v1.1.0: Permissão para editar cobrança (repassado a ChargeDetailsModal) */
  canEdit?: boolean;
  /** v1.1.0: Permissão para excluir/cancelar cobrança (repassado a ChargeDetailsModal) */
  canDelete?: boolean;
  /** v1.2.0: Exibe coluna com o cliente/proprietário do documento (contexto de veículo) */
  showCustomer?: boolean;
  /** v1.2.0: Rótulo da coluna de cliente (default "Proprietário") */
  customerLabel?: string;
}

/** Campos de ordenação */
type SortField = 'descricao' | 'valor_total' | 'status' | 'data_emissao' | 'data_vencimento';

/** Direção da ordenação */
type SortDirection = 'asc' | 'desc';

/** Filtro por status */
type ChargeStatusFilter = 'all' | FinanceiroStatus;

// ============================================================================
// COMPONENTE: HEADER ORDENÁVEL
// ============================================================================

/**
 * Header de coluna com suporte a ordenação
 * Segue mesmo padrão do ContractsTableView
 */
function SortableHeader({
  field,
  label,
  currentField,
  direction,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const isActive = currentField === field;

  return (
    <TableHead
      className={cn(
        'text-xs font-medium py-2 cursor-pointer hover:bg-muted/30 transition-colors select-none',
        align === 'right' && 'text-right pr-4',
        align === 'center' && 'text-center'
      )}
      onClick={() => onSort(field)}
    >
      <div
        className={cn(
          'flex items-center gap-1',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center'
        )}
      >
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

// ============================================================================
// UTILITÁRIO: COR DA BARRA LATERAL POR STATUS
// ============================================================================

/**
 * Retorna a classe de cor para a barra lateral baseado no status da cobrança
 * Segue padrão visual da listagem principal financeira
 */
function getChargeStatusBarColor(status: string): string {
  switch (status) {
    case 'pago': return 'bg-green-500';
    case 'pendente': return 'bg-yellow-500';
    case 'edicao_andamento': return 'bg-amber-500';
    case 'vencido': return 'bg-red-500';
    case 'parcial': return 'bg-blue-500';
    case 'aguardando_pagamento': return 'bg-orange-500';
    case 'cancelado': return 'bg-muted-foreground/40';
    default: return 'bg-muted-foreground/40';
  }
}

// ============================================================================
// COMPONENTE: LINHA DA TABELA
// ============================================================================

/**
 * Linha individual de cobrança na tabela
 */
function ChargeTableRow({
  charge,
  onSelect,
  showCustomer = false,
}: {
  charge: ChargeItem;
  onSelect: (c: ChargeItem) => void;
  /** v1.2.0: exibe a célula de proprietário/cliente */
  showCustomer?: boolean;
}) {
  const statusInfo = STATUS_CONFIG[charge.status] || STATUS_CONFIG.pendente;

  return (
    <TableRow className="hover:bg-muted/40 border-b border-border/40 cursor-pointer group transition-colors" onClick={() => onSelect(charge)}>
      {/* Barra lateral colorida por status */}
      <TableCell className="p-0 w-2">
        <div className={cn('w-1 h-full min-h-[40px] rounded-full', getChargeStatusBarColor(charge.status))} />
      </TableCell>

      {/* Ref. — 8 primeiros dígitos do UUID da cobrança */}
      <TableCell className="px-2 py-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-mono text-[11px] text-muted-foreground cursor-default">
              {charge.id.substring(0, 8).toUpperCase()}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs font-mono">{charge.id}</p>
          </TooltipContent>
        </Tooltip>
      </TableCell>

      {/* Data de vencimento */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {charge.data_vencimento
          ? format(parseDateLocal(charge.data_vencimento), 'dd/MM/yy', { locale: ptBR })
          : '-'
        }
      </TableCell>

      {/* v2.2.0: Proprietário/cliente do documento (opcional) — antes da Descrição */}
      {showCustomer && (
        <TableCell className="text-xs text-muted-foreground px-2 py-1.5 truncate max-w-[150px]">
          {('customer_name' in charge ? (charge as any).customer_name : null) || '-'}
        </TableCell>
      )}

      {/* Descrição */}
      <TableCell className="font-medium max-w-[160px] px-2 py-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate block text-sm">
              {charge.descricao || `Cobrança ${TIPO_CONFIG[charge.tipo_cobranca]}`}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[300px]">
            <p className="text-xs">
              {charge.descricao || `Cobrança ${TIPO_CONFIG[charge.tipo_cobranca]}`}
            </p>
          </TooltipContent>
        </Tooltip>
      </TableCell>




      {/* Status */}
      <TableCell className="px-2 py-1.5">
        <Badge
          className={cn('text-[10px] h-5 shrink-0 whitespace-nowrap px-1.5', statusInfo.color)}
        >
          {statusInfo.label}
        </Badge>
      </TableCell>

      {/* Valor */}
      <TableCell className="text-right px-2 py-1.5">
        <span className="text-sm font-medium">
          {new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: charge.moeda || 'BRL',
          }).format(charge.valor_total)}
        </span>
      </TableCell>

      {/* v1.6.0: Vlr. Restante */}
      <TableCell className="text-right px-2 py-1.5">
        {(() => {
          const recebido = 'valor_recebido' in charge ? Number((charge as any).valor_recebido) : 0;
          const restante = Math.max(charge.valor_total - recebido, 0);
          const isPago = charge.status === 'pago';
          return (
            <span className={cn('text-sm font-medium', isPago ? 'text-green-600 dark:text-green-400' : 'text-foreground')}>
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: charge.moeda || 'BRL',
              }).format(isPago ? 0 : restante)}
            </span>
          );
        })()}
      </TableCell>

      {/* v1.6.0: % Pago (barra de progresso) */}
      <TableCell className="px-2 py-1.5">
        {(() => {
          const recebido = 'valor_recebido' in charge ? Number((charge as any).valor_recebido) : 0;
          const percent = charge.valor_total > 0 ? Math.round((recebido / charge.valor_total) * 100) : 0;
          const isPago = charge.status === 'pago';
          const effectivePercent = isPago ? 100 : Math.min(percent, 100);
          return (
            <div className="flex items-center gap-1.5 min-w-[80px]">
              <Progress
                value={effectivePercent}
                className="h-2 flex-1"
                indicatorClassName={cn(
                  effectivePercent === 100 ? 'bg-green-500' :
                  effectivePercent > 0 ? 'bg-blue-500' :
                  'bg-muted-foreground/30'
                )}
              />
              <span className="text-[10px] text-muted-foreground w-7 text-right">{effectivePercent}%</span>
            </div>
          );
        })()}
      </TableCell>

      {/* Tipo */}
      <TableCell className="px-2 py-1.5">
        <Badge variant="outline" className="text-[10px] h-5">
          {TIPO_CONFIG[charge.tipo_cobranca]}
        </Badge>
      </TableCell>

      {/* Parcelas */}
      <TableCell className="text-center text-xs text-muted-foreground px-2 py-1.5">
        {`${charge.numero_parcelas}x`}
      </TableCell>

      {/* Meio de Pagamento */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {charge.tipo_pagamento
          ? TIPOS_PAGAMENTO.find(t => t.value === charge.tipo_pagamento)?.label || charge.tipo_pagamento
          : '-'}
      </TableCell>

      {/* Conta Corrente */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5 truncate max-w-[120px]">
        {charge.conta_corrente_nome || '-'}
      </TableCell>

      {/* Data de Pagamento */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {charge.primeira_data_pagamento
          ? format(parseDateLocal(charge.primeira_data_pagamento), 'dd/MM/yy', { locale: ptBR })
          : '-'
        }
      </TableCell>

      {/* Data de emissão */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {format(parseDateLocal(charge.data_emissao), 'dd/MM/yy', { locale: ptBR })}
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// STATUS DISPONÍVEIS PARA FILTRO
// ============================================================================

/** Status exibidos nos filtros */
const FILTER_STATUSES: { value: ChargeStatusFilter; label: string; dotColor: string }[] = [
  { value: 'all', label: 'Todos', dotColor: '' },
  { value: 'pendente', label: 'Pendente', dotColor: 'bg-yellow-500' },
  { value: 'edicao_andamento', label: 'Editando', dotColor: 'bg-amber-500' },
  { value: 'aguardando_pagamento', label: 'Aguardando', dotColor: 'bg-orange-500' },
  { value: 'pago', label: 'Pago', dotColor: 'bg-green-500' },
  { value: 'parcial', label: 'Parcial', dotColor: 'bg-blue-500' },
  { value: 'vencido', label: 'Vencido', dotColor: 'bg-red-500' },
  { value: 'cancelado', label: 'Cancelado', dotColor: 'bg-muted-foreground' },
];

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

/**
 * Tabela reutilizável de cobranças financeiras
 * Segue exatamente o padrão visual do ContractsTableView
 */
export function ChargesTableView({
  charges,
  isLoading,
  emptyMessage = 'Nenhuma cobrança encontrada',
  emptyAction,
  canEdit = true,
  canDelete = true,
  showCustomer = false,
  customerLabel = 'Proprietário',
}: ChargesTableViewProps) {
  // ========== ESTADOS ==========
  const [sortField, setSortField] = useState<SortField>('data_vencimento');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [statusFilter, setStatusFilter] = useState<ChargeStatusFilter>('all');
  const [selectedCharge, setSelectedCharge] = useState<ChargeItem | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ========== PROCESSAMENTO DE DADOS ==========

  /** Filtra cobranças por status */
  const filteredCharges = useMemo(() => {
    if (statusFilter === 'all') return charges;
    return charges.filter((c) => c.status === statusFilter);
  }, [charges, statusFilter]);

  /** Ordena cobranças */
  const sortedCharges = useMemo(() => {
    return [...filteredCharges].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'descricao':
          comparison = (a.descricao || '').localeCompare(b.descricao || '');
          break;
        case 'valor_total':
          comparison = a.valor_total - b.valor_total;
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'data_emissao':
          comparison = new Date(a.data_emissao).getTime() - new Date(b.data_emissao).getTime();
          break;
        case 'data_vencimento': {
          const dateA = a.data_vencimento ? new Date(a.data_vencimento).getTime() : 0;
          const dateB = b.data_vencimento ? new Date(b.data_vencimento).getTime() : 0;
          comparison = dateA - dateB;
          break;
        }
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredCharges, sortField, sortDirection]);

  // ========== HANDLERS ==========

  /** Alterna ordenação */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // ========== CONTADORES ==========
  const totalCharges = charges.length;
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    charges.forEach((c) => {
      counts[c.status] = (counts[c.status] || 0) + 1;
    });
    return counts;
  }, [charges]);

  // ========== SKELETON LOADING ==========
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-6 w-14" />
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-border/50 overflow-hidden">
          <div className="overflow-auto">
            <table className="w-max min-w-[700px] caption-bottom text-sm table-fixed">
              <TableHeader>
                <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
                    <TableHead key={i} className="py-2">
                      <Skeleton className="h-3 w-10" />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i} className="border-b border-border/40">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((j) => (
                      <TableCell key={j} className="py-2">
                        <Skeleton className="h-4 w-14" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ========== ESTADO VAZIO ==========
  if (charges.length === 0) {
    return (
      <div className="rounded-lg border border-border/50 bg-muted/20 p-8 text-center">
        <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
          <FileText className="h-6 w-6 text-muted-foreground" />
        </div>
        <h4 className="text-sm font-medium mb-1">{emptyMessage}</h4>
        <p className="text-xs text-muted-foreground mb-3">
          Cobranças são criadas no módulo Financeiro
        </p>
        {emptyAction}
      </div>
    );
  }

  // ========== MIN-WIDTH DINÂMICO ==========
  // v1.2.0: largura mínima maior quando a coluna de proprietário está visível
  const minWidth = showCustomer ? '1410px' : '1260px';


  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 w-full max-w-full overflow-hidden">
      {/* ========== HEADER FIXO: Resumo + Filtros ========== */}
      <div className="flex-shrink-0 space-y-2 pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Resumo */}
          <div className="flex items-center gap-4 text-xs">
            <span className="text-muted-foreground">
              Total: <strong className="text-foreground">{totalCharges}</strong>
            </span>
            {statusCounts.pago && (
              <span className="text-emerald-600">
                Pagos: <strong>{statusCounts.pago}</strong>
              </span>
            )}
            {statusCounts.pendente && (
              <span className="text-yellow-600">
                Pendentes: <strong>{statusCounts.pendente}</strong>
              </span>
            )}
            {statusCounts.vencido && (
              <span className="text-red-600">
                Vencidos: <strong>{statusCounts.vencido}</strong>
              </span>
            )}
          </div>

          {/* Filtros por status */}
          <div className="flex items-center gap-1 flex-wrap">
            {FILTER_STATUSES.map((fs) => (
              <Button
                key={fs.value}
                variant={statusFilter === fs.value ? 'secondary' : 'ghost'}
                size="sm"
                className="h-6 text-xs px-2"
                onClick={() => setStatusFilter(fs.value)}
              >
                {fs.dotColor && (
                  <span className={cn('w-1.5 h-1.5 rounded-full mr-1', fs.dotColor)} />
                )}
                {fs.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Indicador de resultados filtrados */}
        {statusFilter !== 'all' && (
          <p className="text-xs text-muted-foreground">
            Exibindo {sortedCharges.length} de {totalCharges} cobranças
          </p>
        )}
      </div>

      {/* ========== TABELA COM SCROLL ========== */}
      <div className="flex-1 min-h-0 w-full max-w-full min-w-0 rounded-lg border border-border/50 overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="h-full w-full max-w-full overflow-auto"
        >
          <table
            className="w-max caption-bottom text-sm table-fixed"
            style={{ minWidth }}
          >
            {/* Larguras de colunas */}
            <colgroup>
              {/* Larguras críticas via style para garantir render em table-fixed (Tailwind JIT pode não emitir w-[8px]). */}
              <col style={{ width: 8 }} />   {/* Barra de status */}
              <col className="w-[75px]" />   {/* Ref. */}
              <col className="w-[70px]" />   {/* Vencimento */}
              {/* v2.2.0: coluna opcional de proprietário/cliente (antes da Descrição) */}
              {showCustomer && <col className="w-[150px]" />}
              <col className="w-[150px]" />  {/* Descrição */}
              <col className="w-[85px]" />   {/* Status */}
              <col className="w-[95px]" />   {/* Valor */}
              <col className="w-[95px]" />   {/* Vlr. Restante */}
              <col className="w-[110px]" />  {/* % Pago */}
              <col className="w-[80px]" />   {/* Tipo */}
              <col className="w-[50px]" />   {/* Parcelas */}
              <col className="w-[95px]" />   {/* Meio Pagamento */}
              <col className="w-[105px]" />  {/* Conta Corrente */}
              <col className="w-[70px]" />   {/* Data Pagamento */}
              <col className="w-[70px]" />   {/* Emissão */}
            </colgroup>

            {/* Header sticky */}
            <TableHeader className="sticky top-0 bg-background z-[1]">
              <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                <TableHead className="w-2 p-0" /> {/* Barra de status */}
                <TableHead className="text-xs font-medium px-2 py-2">Ref.</TableHead>
                <SortableHeader
                  field="data_vencimento"
                  label="Vencim."
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                {/* v2.2.0: coluna opcional de proprietário/cliente (antes da Descrição) */}
                {showCustomer && (
                  <TableHead className="text-xs font-medium px-2 py-2">{customerLabel}</TableHead>
                )}
                <SortableHeader
                  field="descricao"
                  label="Descrição"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader

                  field="status"
                  label="Status"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="valor_total"
                  label="Valor"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                {/* v1.6.0: Colunas de restante e progresso */}
                <TableHead className="text-xs font-medium text-right px-2 py-1.5">
                  Vlr. Restante
                </TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  % Pago
                </TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Tipo
                </TableHead>
                <TableHead className="text-xs font-medium text-center px-2 py-1.5">
                  Parc.
                </TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Meio Pgto.
                </TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Conta Corrente
                </TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">
                  Data Pgto.
                </TableHead>
                <SortableHeader
                  field="data_emissao"
                  label="Emissão"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
              </TableRow>
            </TableHeader>

            <TableBody>
              {sortedCharges.map((charge) => (
                <ChargeTableRow key={charge.id} charge={charge} showCustomer={showCustomer} onSelect={(c) => { setSelectedCharge(c); setDetailsOpen(true); }} />
              ))}
            </TableBody>
          </table>
        </div>
      </div>

      {/* ========== MODAL DE DETALHES ========== */}
      <ChargeDetailsModal
        cobranca={selectedCharge as FinanceiroCobranca | null}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        canEdit={canEdit}
        canDelete={canDelete}
      />
    </div>
  );
}
